import React, { Component } from 'react';
import { OrderService } from '@wix/cashier-core/dist/src/services/Order';
import { MetadataService } from '@wix/cashier-core/dist/src/services/Metadata';
import { BuyerService } from '@wix/cashier-core/dist/src/services/Buyer';
import { type IAddress } from '@wix/cashier-core/dist/src/services/Order/types';
import { type AppInstance, type RevalidateAppInstance } from '@wix/cashier-common/dist/src/types/AppInstance';
import { type ICountryEntry } from '../../types/CountryEntry';
import { type DeviceTypeEnum } from '../../types/DeviceType';
import { type UserInfo } from '../../types/UserInfo';
import { transformCountryKeyToApiFormat } from '../../utils/countryFieldFormat';
import { trackError } from '../../utils/errorReporter';
import { UserInfoFormView } from './view';

export interface UserInfoFormControllerProps {
  host: string;
  locale: string;
  snapshotId: string;
  visitorId: string;
  appearance: DeviceTypeEnum;
  initialUserInfo?: UserInfo;
  disabled: boolean;

  onChange?(isValid: boolean, formData: any, isChangedProgrammatically?: boolean): void;
  startModuleLoad(key: string): void;
  endModuleLoad(key: string): void;
  getAppInstance(): AppInstance;
  revalidateAppInstance: RevalidateAppInstance;
}

interface UserInfoFormControllerState {
  resolvedCountry?: string;
  isCountriesLoading: boolean;
  isResolvingCountry: boolean;
  countries: ICountryEntry[];
}

export class UserInfoFormController extends Component<UserInfoFormControllerProps, UserInfoFormControllerState> {
  state: UserInfoFormControllerState = {
    isCountriesLoading: true,
    isResolvingCountry: !this.props.initialUserInfo?.country,
    countries: [],
  };

  private userInfoForm: UserInfoFormView;

  public componentAPI = {
    save: (params?: { skipContactCreation?: boolean }) => {
      const buyerInfoAll = transformCountryKeyToApiFormat(this.userInfoForm.componentAPI.getFormData());

      const buyerInfo: Partial<IAddress> = {};

      // Removing empty strings
      // Missing key or `null` value is okay
      Object.keys(buyerInfoAll).forEach((key: string) => {
        if (buyerInfoAll[key] !== '') {
          buyerInfo[key] = buyerInfoAll[key];
        }
      });

      const orderService = new OrderService({
        baseURL: this.props.host,
        appInstance: this.props.getAppInstance(),
        revalidateAppInstance: this.props.revalidateAppInstance,
      });

      if (params?.skipContactCreation) {
        return orderService
          .updateBuyerInfo(this.props.snapshotId, {
            ...(this.props.visitorId && {
              buyerInfo: {
                buyerId: this.props.visitorId,
              },
            }),
            shippingAddress: buyerInfo,
            billingAddress: buyerInfo,
          })
          .catch((error) => {
            trackError(error, {
              messagePrefix: 'UserInfoFormController: Failed to update order',
            });
            throw new Error('UserInfoFormController: Failed to update order');
          });
      }

      return orderService
        .createContactAndUpdateOrder({
          orderId: this.props.snapshotId,
          buyerInfo,
        })
        .catch((error) => {
          trackError(error, {
            messagePrefix: 'UserInfoFormController: Failed to create contact and update order',
          });
          throw new Error('UserInfoFormController: Failed to create contact and update order');
        });
    },
    validate: () => {
      return this.userInfoForm.componentAPI.validate();
    },
    markInvalidFields: (): void => {
      this.userInfoForm.componentAPI.markInvalidFields();
    },
    getFormData: (): UserInfo => {
      return this.userInfoForm.componentAPI.getFormData();
    },
  };

  private initCountries = async () => {
    const { locale, host } = this.props;

    this.props.startModuleLoad('state:countries');

    let countries: ICountryEntry[];

    try {
      countries = await new MetadataService({
        baseURL: host,
        appInstance: this.props.getAppInstance(),
        revalidateAppInstance: this.props.revalidateAppInstance,
      }).fetchCountries(locale);
    } catch (error) {
      trackError(error, {
        messagePrefix: 'UserInfoFormController: Failed to fetch countries',
      });
      // TODO: handle error
    }

    this.props.endModuleLoad('state:countries');

    this.setState({
      isCountriesLoading: false,
      countries: countries || [],
    });
  };

  private resolveCountry = async () => {
    if (!this.props.initialUserInfo || !this.props.initialUserInfo.country) {
      this.props.startModuleLoad('state:resolveCountry');

      let country: string;

      try {
        country = await new BuyerService({
          baseURL: this.props.host,
          appInstance: this.props.getAppInstance(),
          revalidateAppInstance: this.props.revalidateAppInstance,
        }).resolveCountry();
      } catch (error) {
        trackError(error, {
          messagePrefix: 'UserInfoFormController: Failed to resolve the country',
        });
        // do nothing, UoU should enter country manually
      }

      this.props.endModuleLoad('state:resolveCountry');

      this.setState({
        resolvedCountry: country,
        isResolvingCountry: false,
      });
    }
  };

  componentDidMount() {
    this.resolveCountry();
    this.initCountries();
  }

  private get isLoading(): boolean {
    return this.state.isCountriesLoading || this.state.isResolvingCountry;
  }

  render() {
    if (this.isLoading) {
      return null;
    }

    return (
      <UserInfoFormView
        disabled={this.props.disabled}
        ref={(userInfoForm) => (this.userInfoForm = userInfoForm)}
        appearance={this.props.appearance}
        countries={this.state.countries}
        initialUserInfo={this.props.initialUserInfo}
        resolvedCountry={this.state.resolvedCountry}
        onChange={this.props.onChange}
      />
    );
  }
}
